import debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';
import useBodyScrollDisable from '#hooks/useBodyScrollDisable';
import { SNAP_HEIGHT } from './constants';
import * as S from './styles';

export interface DaySelectOption {
  label?: string;
  value: string;
}

interface Props {
  defaultValue?: string;
  width?: number;
  options: DaySelectOption[];
  surfix?: string;
  onChange?: (value: string) => void;
}

export default function ScrollContainer({
  width,
  options,
  defaultValue,
  surfix,
  onChange
}: Props) {
  const scrollRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>();
  const isInitialMount = useRef<boolean>(true);

  useBodyScrollDisable();

  const handleScroll = debounce(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const index = Math.floor(container.scrollTop / SNAP_HEIGHT);
      setActiveIndex(index);

      if (onChange && !isInitialMount.current) {
        onChange(options[index]?.value);
      }
    }
  }, 100);

  const handleScrollTo = (index: number) => {
    if (scrollRef.current && typeof index === 'number') {
      scrollRef.current.scrollTo({
        top: index * SNAP_HEIGHT,
        behavior: isInitialMount.current ? 'auto' : 'smooth'
      });
    }
  };

  const handleClick = (value: string, index: number) => {
    setActiveIndex(index);
    handleScrollTo(index);
  };

  useEffect(() => {
    if (defaultValue && !activeIndex) {
      const index = options.findIndex(item => item?.value === defaultValue);
      handleScrollTo(index);
    }

    const container = scrollRef.current;
    container?.addEventListener('scroll', handleScroll);
    return () => container?.removeEventListener('scroll', handleScroll);
  }, [defaultValue]);

  useEffect(() => {
    setTimeout(() => (isInitialMount.current = false), 10);
  }, []);

  return (
    <S.Container containerWidth={width}>
      <S.ScrollContainer>
        <S.Holder />
        <S.Scrollable ref={scrollRef}>
          {options.map((item, index) => (
            <div key={index}>
              <S.ButtonSelect
                selected={index === activeIndex}
                onClick={() => handleClick(item?.value, index)}
              >
                {item?.label ?? item?.value}
              </S.ButtonSelect>
            </div>
          ))}
        </S.Scrollable>
      </S.ScrollContainer>
      <S.Surfix>{surfix}</S.Surfix>
    </S.Container>
  );
}
